import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatPrice } from '../utils/priceFormatter';
import { InsuranceBadgeSmall } from './InsuranceBadge';
import { ChronoBadgeSmall } from './ChronoBadge';
import { isInsuranceEligible } from '../utils/insuranceEligibility';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ProductCard = ({ product, viewMode, productType, collectionName, hideNavigation = false }) => {
  const navigate = useNavigate();
  const [pricePerM2, setPricePerM2] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(true);
  const [imageReady, setImageReady] = useState(false);
  const [cachedSrc, setCachedSrc] = useState(null);
  const preloadRef = useRef(null);
  
  // Check if this product is eligible for insurance
  const showInsuranceBadge = isInsuranceEligible(productType);
  
  // Check if this product is eligible for Chrono service (S&P CHRONOS collection)
  const showChronoBadge = collectionName?.toLowerCase().includes('chrono');
  
  const handleProductClick = () => {
    // Si hideNavigation est true, ne pas naviguer (le parent gère le clic)
    if (hideNavigation) return;
    
    const url = productType 
      ? `/produit/${product.id}?type=${productType}`
      : `/produit/${product.id}`;
    navigate(url);
  };

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/calculate/price-per-m2/${product.id}`);
        setPricePerM2(response.data.price_per_m2);
      } catch (error) {
        console.error('Error fetching price:', error);
        setPricePerM2(null);
      } finally {
        setLoadingPrice(false);
      }
    };

    if (product.id) {
      fetchPrice();
    }
  }, [product.id]);

  // Preload image in memory before displaying
  useEffect(() => {
    const imageUrl = product.thumbnail || product.image;
    
    // Reset state when product changes
    setImageReady(false);
    setCachedSrc(null);
    
    // Cancel any previous preload
    if (preloadRef.current) {
      preloadRef.current.onload = null;
      preloadRef.current.onerror = null;
      preloadRef.current = null;
    }
    
    if (!imageUrl) return;
    
    // Create new Image object to preload in memory
    const img = new Image();
    preloadRef.current = img;
    
    img.onload = () => {
      // Image is now fully cached by browser
      setCachedSrc(imageUrl);
      setImageReady(true);
    };
    
    img.onerror = () => {
      // On error, still mark as ready to stop skeleton
      setImageReady(true);
      setCachedSrc(null);
    };
    
    // Start loading
    img.src = imageUrl;
    
    return () => {
      if (preloadRef.current) {
        preloadRef.current.onload = null;
        preloadRef.current.onerror = null;
      }
    };
  }, [product.id, product.thumbnail, product.image]);

  const displayPrice = () => {
    if (loadingPrice) {
      return <span className="text-gray-400">...</span>;
    }
    if (pricePerM2 === null || pricePerM2 === undefined) {
      return <span className="text-gray-500 text-sm">Prix sur demande</span>;
    }
    return <span>À partir de {formatPrice(pricePerM2)} €</span>;
  };

  // Skeleton component for loading state
  const ImageSkeleton = ({ className }) => (
    <div className={`${className} skeleton-shimmer`}></div>
  );

  if (viewMode === 'list') {
    return (
      <div 
        onClick={handleProductClick}
        className="flex gap-4 bg-white border rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer"
      >
        <div className="relative w-32 h-32">
          {/* Insurance Badge */}
          {showInsuranceBadge && <InsuranceBadgeSmall className="!top-1 !left-1 !text-[10px] !px-1.5 !py-0.5" />}
          {/* Chrono Badge */}
          {showChronoBadge && <ChronoBadgeSmall className="!top-1 !right-1 !text-[10px] !px-1.5 !py-0.5" />}
          {/* Show skeleton until image is preloaded and ready */}
          {!imageReady && <ImageSkeleton className="absolute inset-0 rounded" />}
          {/* Only render img when image is fully preloaded in memory */}
          {imageReady && cachedSrc && (
            <img
              src={cachedSrc}
              alt={product.ref_frs || product.code1 || product.full_code}
              className="w-32 h-32 object-cover rounded absolute inset-0"
            />
          )}
        </div>
        <div className="flex-1">
          <p className="text-xs text-gray-500 mb-1">{product.gamme}</p>
          <h3 className="font-medium text-gray-900 mb-2">{product.ref_frs || product.code1 || product.full_code}</h3>
          <p className="text-sm text-gray-600 mb-2">Gamme : {product.gamme}</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-celadon">{displayPrice()}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={handleProductClick}
      className="bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-all group cursor-pointer"
    >
      <div className="relative overflow-hidden h-64">
        {/* Insurance Badge */}
        {showInsuranceBadge && <InsuranceBadgeSmall />}
        {/* Chrono Badge */}
        {showChronoBadge && <ChronoBadgeSmall />}
        {/* Show skeleton until image is preloaded and ready */}
        {!imageReady && <ImageSkeleton className="absolute inset-0 w-full h-full" />}
        {/* Only render img when image is fully preloaded in memory */}
        {imageReady && cachedSrc && (
          <img
            src={cachedSrc}
            alt={product.ref_frs || product.code1 || product.full_code}
            className="w-full h-64 object-cover group-hover:scale-105 transition-all duration-300 absolute inset-0"
          />
        )}
      </div>
      <div className="p-4">
        <p className="text-xs text-gray-500 mb-1">{product.gamme}</p>
        <h3 className="font-medium text-gray-900 mb-2 text-sm line-clamp-2">{product.ref_frs || product.code1 || product.full_code}</h3>
        <p className="text-xs text-gray-600 mb-3">Supplier : {product.supplier || 'N/A'}</p>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-bold text-celadon">{displayPrice()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;